/**
 * Tasks — My Day
 * The rep's daily operating system: Next Best Action, buckets, neglected leads.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import useAuthStore from '../lib/auth-store';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Plus, Sparkles, PhoneCall, Clock3, Users2, FileText, AlertTriangle,
  Inbox, CalendarClock, RefreshCw, PauseCircle, Sun,
} from 'lucide-react';
import { TaskCard } from '../components/tasks/TaskCard';
import { NewTaskModal } from '../components/tasks/NewTaskModal';
import { CompleteTaskModal } from '../components/tasks/CompleteTaskModal';
import { WeeklyDigestModal } from '../components/tasks/WeeklyDigestModal';

const BUCKETS = [
  { key: 'overdue', label: 'Overdue', icon: AlertTriangle, color: 'text-red-500' },
  { key: 'due_today', label: 'Due Today', icon: Inbox, color: 'text-blue-500' },
  { key: 'calls', label: 'Calls', icon: PhoneCall, color: 'text-emerald-500' },
  { key: 'follow_ups', label: 'Follow-Ups', icon: Clock3, color: 'text-amber-500' },
  { key: 'meetings', label: 'Meetings', icon: Users2, color: 'text-violet-500' },
  { key: 'applications', label: 'Applications', icon: FileText, color: 'text-cyan-500' },
  { key: 'snoozed', label: 'Waiting', icon: PauseCircle, color: 'text-fuchsia-500' },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function TasksPage() {
  const { user } = useAuthStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBucket, setActiveBucket] = useState('due_today');
  const [showNew, setShowNew] = useState(false);
  const [completingTask, setCompletingTask] = useState(null);
  const [showDigest, setShowDigest] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.getMyDay();
      setData(d);
    } catch (e) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSnooze = async (task, option) => {
    try {
      await api.snoozeTask(task.id, { option });
      toast.success(`Snoozed — ${option.replace(/_/g, ' ')}`);
      load();
    } catch (e) {
      toast.error('Failed to snooze task');
    }
  };

  const handleReactivate = async (task) => {
    try {
      await api.updateTask(task.id, { status: 'open' });
      toast.success('Task reactivated');
      load();
    } catch (e) {
      toast.error('Failed to reactivate task');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground" data-testid="tasks-loading">Loading your day…</div>;
  }

  const activeTasks = data?.[activeBucket] || [];
  const nba = data?.next_best_action;
  const neglected = data?.neglected || [];

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">{greeting()}, {user?.full_name?.split(' ')[0] || 'there'}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data.counts.overdue} overdue · {data.counts.due_today} due today · {data.counts.calls} calls · {data.counts.meetings} meetings
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={load} data-testid="tasks-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setShowDigest(true)} data-testid="view-weekly-digest-btn"><Sun className="h-4 w-4 mr-2" /> This Week's Digest</Button>
          <Button onClick={() => setShowNew(true)} data-testid="new-task-btn"><Plus className="h-4 w-4 mr-2" /> New Task</Button>
        </div>
      </div>

      {nba && (
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5" data-testid="next-best-action-card">
          <div className="flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wide">
            <Sparkles className="h-4 w-4" /> Next Best Action
          </div>
          <p className="text-lg font-semibold mt-2">{nba.title}</p>
          {nba.subject_name && <p className="text-sm text-muted-foreground">{nba.subject_name}</p>}
          <p className="text-sm mt-2 text-foreground/80">{nba.reason}</p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={() => setCompletingTask(nba)} data-testid="nba-complete-btn">Mark Done</Button>
            <Button size="sm" variant="outline" onClick={() => handleSnooze(nba, 'tomorrow')} data-testid="nba-snooze-btn">Snooze to Tomorrow</Button>
          </div>
        </div>
      )}

      {neglected.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="neglected-banner">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-600">
            <AlertTriangle className="h-4 w-4" /> {neglected.length} opportunit{neglected.length === 1 ? 'y needs' : 'ies need'} attention
          </div>
          <div className="mt-2 space-y-1">
            {neglected.slice(0, 3).map((n) => (
              <p key={n.deal_id} className="text-xs text-muted-foreground" data-testid={`neglected-item-${n.deal_id}`}>
                <span className="font-medium text-foreground">{n.merchant_name}</span> — {n.stage} — {n.reasons.join(', ')}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="task-bucket-tabs">
        {BUCKETS.map(({ key, label, icon: Icon, color }) => (
          <button
            key={key}
            onClick={() => setActiveBucket(key)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm whitespace-nowrap border transition-colors ${
              activeBucket === key ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'
            }`}
            data-testid={`bucket-tab-${key}`}
          >
            <Icon className={`h-3.5 w-3.5 ${activeBucket === key ? '' : color}`} />
            {label}
            <Badge
              variant="outline"
              className={`ml-1 text-[10px] px-1.5 ${activeBucket === key ? 'bg-white/20 text-primary-foreground border-white/30' : ''}`}
            >
              {data.counts[key] ?? 0}
            </Badge>
          </button>
        ))}
      </div>

      <div className="space-y-2.5" data-testid="task-bucket-list">
        {activeTasks.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="task-bucket-empty">
            <CalendarClock className="h-8 w-8 mx-auto mb-2 opacity-40" />
            Nothing here — you're on top of it.
          </div>
        ) : (
          activeTasks.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              isOverdue={activeBucket === 'overdue'}
              onComplete={setCompletingTask}
              onSnooze={handleSnooze}
              onReactivate={activeBucket === 'snoozed' ? handleReactivate : undefined}
            />
          ))
        )}
      </div>

      <NewTaskModal open={showNew} onClose={() => setShowNew(false)} onCreated={load} />
      <CompleteTaskModal task={completingTask} onClose={() => setCompletingTask(null)} onDone={load} />
      <WeeklyDigestModal open={showDigest} onClose={() => setShowDigest(false)} />
    </div>
  );
}
